import {
    DateField,
    EditButton,
    NumberField,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, FileText, Layers, Package, User, ArrowLeft } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';

const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/boms" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back to BOMs
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight text-slate-900 ml-1">{record.reference}</span>
        </div>
    );
};
import { StatusBadge } from '../documents/shared/StatusBadge';
import type { BoM, BoMItem } from '../types';
// Note: BoMItem now uses description + hsn_code (not inventory_item_id)
import { ApproveBoMButton } from './ApproveBoMButton';
import { CreateQuotationFromBoMButton } from './CreateQuotationFromBoMButton';
import { CreateWorkOrderButton } from './CreateWorkOrderButton';
import { GeneratePOButton } from './GeneratePOButton';
import { PreProductionChecklist } from './PreProductionChecklist';
import { SupersedeBoMButton } from './SupersedeBoMButton';

const BoMItemsTable = ({ items }: { items: BoMItem[] }) => {
    if (!items || items.length === 0) return (
        <div className="py-8 text-center text-slate-400 text-sm italic">No items added to this BoM.</div>
    );

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow className="bg-slate-50/50">
                        <TableHead className="w-[50px]">Sr.</TableHead>
                        <TableHead>Material / Component</TableHead>
                        <TableHead>HSN / SAC</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Est. Unit Cost</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((item, index) => (
                        <TableRow key={item.id}>
                            <TableCell className="font-mono text-xs text-slate-400">{index + 1}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <span className="font-semibold text-slate-900">{(item as any).description || '—'}</span>
                                    {item.notes && <p className="text-[10px] text-slate-500 italic">{item.notes}</p>}
                                </div>
                            </TableCell>
                            <TableCell>
                                <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                    {(item as any).hsn_code || '—'}
                                </span>
                            </TableCell>
                            <TableCell className="text-right font-bold">{item.quantity}</TableCell>
                            <TableCell>{item.unit}</TableCell>
                            <TableCell className="text-right">
                                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.estimated_unit_cost)}
                            </TableCell>
                            <TableCell className="text-right font-bold text-slate-900">
                                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(item.quantity * item.estimated_unit_cost)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-slate-50 font-bold">
                        <TableCell colSpan={6} className="text-right uppercase text-xs tracking-wider text-slate-500">Grand Total (Estimated)</TableCell>
                        <TableCell className="text-right text-lg text-blue-600">
                            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(items.reduce((acc, curr) => acc + (curr.quantity * curr.estimated_unit_cost), 0))}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
};

const BoMShowContent = () => {
    const record = useRecordContext<BoM>();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            {/* Header Section */}
            <div className="flex justify-between items-start">
                <div className="flex gap-6 items-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center border-2 border-slate-200 shadow-sm">
                        <Layers className="w-8 h-8 text-slate-400" />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <h2 className="text-3xl font-bold tracking-tight text-slate-900">{record.reference}</h2>
                            <StatusBadge status={record.status} />
                            <Badge variant="outline" className="font-mono text-xs">v{record.version}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                                <ReferenceField source="project_id" reference="projects" link="show">
                                    <TextField source="name" className="text-primary hover:underline font-medium cursor-pointer" />
                                </ReferenceField>
                            </div>
                            <span className="text-slate-300">|</span>
                            <div className="flex items-center gap-1 font-semibold text-green-700">
                                <DollarSign className="w-4 h-4" />
                                <NumberField source="total_estimated_cost" options={{ style: 'currency', currency: 'INR' }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="materials" className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
                    <TabsTrigger value="materials">Material Breakdown</TabsTrigger>
                    <TabsTrigger value="details">Audit & Details</TabsTrigger>
                </TabsList>

                <TabsContent value="materials" className="space-y-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                <Package className="w-4 h-4" /> Bill of Materials Items
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <BoMItemsTable items={record.items} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="details">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                    <FileText className="w-4 h-4" /> Design Context
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase">Linked Design Request</span>
                                    <div>
                                        <ReferenceField source="design_request_id" reference="design_requests" link="show">
                                            <TextField source="reference" className="text-blue-600 font-bold" />
                                        </ReferenceField>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <PreProductionChecklist />

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                                    <User className="w-4 h-4" /> Approval Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4 text-sm font-medium">
                                <div className="flex justify-between items-center py-2 border-b border-dashed">
                                    <span className="text-slate-500">Created At</span>
                                    <DateField source="created_at" />
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-dashed">
                                    <span className="text-slate-500">Approved By</span>
                                    <ReferenceField source="approved_by" reference="sales" link="show">
                                        <TextField source="first_name" />
                                    </ReferenceField>
                                </div>
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-slate-500">Approved At</span>
                                    <DateField source="approved_at" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

const BoMShowActions = () => (
    <div className="flex items-center gap-2">
        <CreateWorkOrderButton />
        <CreateQuotationFromBoMButton />
        <ApproveBoMButton />
        <SupersedeBoMButton />
        <GeneratePOButton />
        <EditButton />
    </div>
);

export const BoMShow = () => (
    <Show title={<ShowTitle />} actions={<BoMShowActions />}>
        <BoMShowContent />
    </Show>
);
